import PropTypes from 'prop-types';

export const CALCULATION_TYPES = {
  CERTIFICATION: 'certification',
  SECTION: 'section',
  QUESTION: 'question',
  QUESTION_GROUP: 'question_group',
};

export const VARIABLE_TYPES = {
  CELL: 'cell',
  COLUMN: 'column',
};

export const INVALID_CALC_SOURCE_QUESTION_TYPES = [
  'text',
  'log_number',
  'text_area',
  'email',
  'ssn',
  'phone',
  'zip_code',
  'account',
  'last_SSN',
];

export const VALID_CALC_SOURCE_QUESTION_TYPES = [
  'file',
  'date',
  'currency',
  'bool',
  'choices',
  'multiselect',
  'number',
  'frequency',
  'calculation',
  'percentage',
  'eligibility_code',
  'name',
];

export const VARIABLE_OPTIONS_TOOLTIP = {
  [VARIABLE_TYPES.CELL]: {
    question:
      'The available options for this variable are:\n' +
      `- All non-child questions in the same group except ${INVALID_CALC_SOURCE_QUESTION_TYPES.join(
        ', '
      )} questions.`,
    question_group:
      'The available options for this variable are:\n' +
      '- Other group calculations of this question group (excluding ones that depend on the current calculation).\n' +
      '- All questions of single groups except ' +
      `${INVALID_CALC_SOURCE_QUESTION_TYPES.join(', ')} questions.`,
    section:
      'The available options for this variable are:\n' +
      '- Other section calculations of this section (excluding household calculations) with a valid member type.\n' +
      '- Group calculations in the same section with a valid member type.\n' +
      '- All questions of single groups except ' +
      `${INVALID_CALC_SOURCE_QUESTION_TYPES.join(', ')} questions.`,
    household:
      'The available options for this variable are:\n' +
      '- All household calculations (except those that use this one as a variable source).',
  },
  [VARIABLE_TYPES.COLUMN]: {
    question:
      'The available options for this variable are:\n' +
      `- All child questions of file questions in this question group, except ${INVALID_CALC_SOURCE_QUESTION_TYPES.join(
        ', '
      )} questions.`,
    question_group:
      'The available options for this variable are:\n' +
      `- All child questions of file questions in this question group, except ${INVALID_CALC_SOURCE_QUESTION_TYPES.join(
        ', '
      )} questions.\n` +
      '- All questions in multi group except ' +
      `${INVALID_CALC_SOURCE_QUESTION_TYPES.join(', ')} questions.`,
    section:
      'The available options for this variable are:\n' +
      `- All child questions of file questions in this question group, except ${INVALID_CALC_SOURCE_QUESTION_TYPES.join(
        ', '
      )} questions.\n` +
      '- All questions in multi group except ' +
      `${INVALID_CALC_SOURCE_QUESTION_TYPES.join(', ')} questions.`,
    household:
      'The available options for this variable are:\n' +
      '- All Section calculations with a valid member type.\n' +
      '- All Group calculations with a valid member type.\n' +
      '- All questions except ' +
      `${INVALID_CALC_SOURCE_QUESTION_TYPES.join(', ')} questions.`,
  },
};

export const TYPES = {
  questionnaire: 'questionnaire',
  section: 'section',
  page: 'page',
  question: 'question',
  questionGroup: 'question_group',
  questionChoice: 'question_choice',
  contentType: 'content_type',
  miscellaneous: 'miscellaneous',
  signature: 'signature',
  document: 'document',
  mit: 'mit',
  sit: 'sit',
  sitDate: 'sitDate',
  sitName: 'sitName',
  sitHhmt: 'sitHhmt',
  sitPhone: 'sitPhone',
  sitInitials: 'sitInitials',
  sitUnassigned: 'sitUnassigned',
  sitUnsaved: 'sitUnsaved',
  questionContext: 'questionContext',
  calculation: 'calculation',
  mappingTableCells: 'mappingTableCells',
};

export const eSignInstanceConfig = {
  licenseKey: import.meta.env.VITE_PDF_LICENSE_KEY,
  path: '/webviewer/lib',
  streaming: false,
  css: '/pdf.css' /* https://pdfjs.community/t/extra-space-appears-below-toolbar-in-small-screen-size/966 */,
};

export const DISABLED_ELEMENTS = [
  'linkButton',
  'documentControl',
  'outlinesPanelButton',
  'notesPanelButton',
  'notesPanel',
  'outlinesPanel',
  'freeHandToolGroupButton',
  'textToolGroupButton',
  'shapeToolGroupButton',
  'eraserToolButton',
  'stickyToolButton',
  'miscToolGroupButton',
  'signatureToolButton',
  'freeTextToolButton',
  'textHighlightToolButton',
  'textUnderlineToolButton',
  'textSquigglyToolButton',
  'textStrikeoutToolButton',
  'textPopup',
  'contextMenuPopup',
  'richTextItalicButton',
  'richTextUnderlineButton',
  'richTextColorPalette',
  'richTextBoldButton',
  'richTextStrikeButton',
  'toolsHeader',
  'ribbonsDropdown',
  'toggleNotesButton',
  'annotationStyleEditButton',
  'highlightToolGroupButton',
  'ribbons',
  'undoButton',
  'redoButton',
  'toolsOverlay',
  'underlineToolGroupButton',
  'strikeoutToolGroupButton',
  'squigglyToolGroupButton',
  'stickyToolGroupButton',
  'freeTextToolGroupButton',
  'freeHandHighlightToolGroupButton',
  'printButton',
  'downloadButton',
  'thumbRotateClockwise',
  'thumbDelete',
  'pageManipulationOverlayButton',
];

const { any, arrayOf, bool, number, shape, string } = PropTypes;

export const questionShape = shape({
  html: string,
  id: number,
  isRequired: bool,
  name: string,
  order: number,
  page: number,
  parent_question: any,
  question_file_example: arrayOf(any),
  title: string,
  type: shape({
    autoComplete: string,
    choices: string,
    colCount: any,
    dateFormat: string,
    id: number,
    inputFormat: string,
    inputMask: string,
    inputType: string,
    mappable: bool,
    maxSize: number,
    panelAddText: string,
    panelRemoveText: string,
    showPreview: any,
    startWithNewLine: any,
    storeDataAsText: any,
    type: string,
  }),
  validators: arrayOf(any),
  visibleIf: any,
  visibleIfNot: any,
});

export const pageShape = shape({
  content_type_id: number,
  id: number,
  name: string,
  order: any,
  questions: arrayOf(questionShape),
  section: number,
});

export const sectionShape = shape({
  content_type_id: number,
  id: number,
  name: string,
  order: any,
  pages: arrayOf(pageShape),
});

export const DEFAULT_SECTION = {
  name: '',
  help_text: '',
  id: '',
  notSavedSection: true,
  wasCreated: false,
  firstQitOrder: 0,
  lastQitOrder: 0,
};

export const DEFAULT_PAGE = {
  name: '',
  help_text: '',
  id: '',
  notSaved: true,
  wasCreated: false,
  firstQitOrder: 0,
  lastQitOrder: 0,
};

export const QUESTION_GROUP_TYPES = {
  single: 'single',
  multi: 'multi',
};

export const DEFAULT_GROUP = {
  id: null,
  type: QUESTION_GROUP_TYPES.single,
  name: '',
  age_min: '',
  age_max: '',
  help_text: '',
  question: null,
  question_choice: null,
  householdmember_type: [],
  shouldOpen: true,
  firstQitOrder: 0,
  lastQitOrder: 0,
};

export const DEFAULT_QUESTION = {
  qit: null,
  name: '',
  help_text: '',
  id: '',
  necessity: '3',
  number_of_answers_required: 1,
  for_compliance: false,
  question_context: null,
  number_of_files_required_per_answer: '',
  number_of_pages_required_per_file: '',
};

export const QUESTIONS_TYPES_WITHOUT_CHOICES_TO_RENDER = ['ssn'];

const symbolRenderType = {
  id: 'SymbolRender',
  displayName: 'Symbol (if the value is not None, render a symbol)',
};

export const MISC_TIC_HAS_ANSWERS_RENDER_TYPES = [
  {
    id: 'TextRender',
    displayName: 'Text',
    description:
      'The value of this answer will be placed in the PDF as a normal text value with no modifications.',
  },
  {
    id: 'SymbolRender',
    displayName: 'Symbol',
    description: 'Symbol (if the value is not None, render a symbol)',
  },
];

export const MISC_TIC_MATCH_ANSWERS_RENDER_TYPES = [
  {
    id: 'TextRender',
    displayName: 'Text',
    description:
      'The value of this answer will be placed in the PDF as a normal text value with no modifications.',
  },
  {
    id: 'SymbolRender',
    displayName: 'Symbol',
    description: 'Symbol (if the value is not None, render a symbol)',
  },
];

export const RENDER_TYPES = {
  TextRender: {
    id: 'TextRender',
    displayName: 'Text',
    description:
      'The value of this answer will be placed in the PDF as a normal text value with no modifications.',
  },
  InitialsRender: {
    id: 'InitialsRender',
    displayName: 'Only Initials',
    description:
      'The value of this context will render the first letter of each answer.',
  },
  SymbolRender: {
    displayName: 'Symbol',
    description:
      'The value of this answer will not be used, instead an X will be placed in the PDF as text.',
  },
  CustomValueRender: {
    displayName: 'Custom Value',
    description:
      'The value of this answer will not be used, instead the following value will display in the PDF text.',
  },
  HiddenSSNRender: {
    displayName: 'Hidden SSN Render',
    description:
      'The value for this answer will be hide allowing only the last 4 digits (***-**-9999).',
  },
  ListRender: {
    id: 'ListRender',
    displayName: 'List of answers',
    description:
      'The answers will be organized into a simple list, with one answer as text on each line. Answers will be sorted alphabetically.',
  },
  NumberedListRender: {
    id: 'NumberedListRender',
    displayName: 'Numbered list of answers',
    description:
      'The answers will be organized into a numbered list, with one answer as text on each line. Answers will be sorted alphabetically.',
  },
  DateRender: {
    displayName: 'Date',
    description:
      'The answer will be formatted into a date and then placed in the PDF as text.',
  },
  JoinedStringRender: {
    id: 'JoinedStringRender',
    displayName: 'Text List of Answers',
    description:
      'The answers will be organized into a simple list, with one answer as text followed by a comma and then the next answer. Answers will be sorted alphabetically.',
  },
  Calculation: {
    id: 'Calculation',
    displayName: 'Calculation',
    description: 'Calculation rendert type.',
  },
  SUM: {
    id: 'SUM',
    displayName: 'SUM',
    description: 'Sums all the values of the selected household members.',
  },
  AVG: {
    id: 'AVG',
    displayName: 'AVG',
    description: 'Average value of the selected household members.',
  },
  WithMoneySignRender: {
    id: 'WithMoneySignRender',
    displayName: 'With Money Sign',
    description:
      'The value of this answer will be placed in the PDF as a currency with money sign (Dollar sign).',
  },
  WithoutMoneySignRender: {
    id: 'WithoutMoneySignRender',
    displayName: 'Without Money Sign',
    description:
      'The value of this answer will be placed in the PDF as a currency without money sign (Dollar sign).',
  },
  AnswerGroupCount: {
    id: 'AnswerGroupCount',
    displayName: 'Answer Groups Count',
    description:
      'The value of this answer will be the count of all the answer groups for this question.',
  },
  PercentWithSymbol: {
    id: 'PercentWithSymbol',
    displayName: 'Percent With Symbol',
    description: 'The value will print with the percentage (%) symbol.',
  },
  PercentWithoutSymbol: {
    id: 'PercentWithoutSymbol',
    displayName: 'Percent Without Symbol',
    description: 'The value will print without the percentage (%) symbol.',
  },
};

export const GROUP_RENDER_TYPES = {
  RENDER_ONE_PER_PAGE: {
    id: 'RENDER_ONE_PER_PAGE',
    displayName: 'Render One page Per Answer Group',
    description: `This option would allow mapping of all questions in question group
    using their existing question type options to map. One generation, one populated page would appear per
    answer group.`,
  },
  RENDER_ALL_ONE_PAGE: {
    id: 'RENDER_ALL_ONE_PAGE',
    displayName: 'Render Multiple Answer Groups in a Single Page',
    description: `This option would allow mapping of multiple answer groups
    on a single rendered page. Since you are mapping more than one thing, these values will always rendered as a list
    of the mapped question type and result in one generated page.`,
  },
};

export const CONTENT_TYPES = {
  certificationconfig: {
    fields: ['attr_'],
    name: 'Certification Config',
    endpoint: 'certification/certification_config/',
  },
  user: {
    fields: [
      'first_name',
      'middle_name',
      'last_name',
      'full_name',
      'email',
      'phone_number',
    ],
    name: 'User',
    endpoint: 'user_profile/user/',
  },
  userprofile: {
    fields: [
      'date_of_birth',
      'ssn',
      'gender',
      'disability_status',
      'disability_types',
      'race_ethnicity',
      'asian_sub',
      'pacific_islander_sub',
      'ethnicity',
      'citizenship_status',
    ],
    name: 'User Profile',
    endpoint: 'user_profile/profile/',
  },
  certification: {
    fields: [
      'current_date',
      'effective_date',
      'log_number',
      'lease_start',
      'lease_end',
      'move_in_date',
      'move_out_date',
    ],
    name: 'Certification',
    endpoint: 'certification/certification/',
  },
  householdmembertype: {
    fields: ['display_name'],
    name: 'Household Member Type',
    endpoint: 'household/household_member_type/',
  },
  householdmember: {
    fields: [
      'first_name',
      'middle_name',
      'last_name',
      'email_addresses',
      'mobile_phones',
    ],
    name: 'Household Member',
    endpoint: 'household/household_member/',
  },
  household: {
    fields: ['preference_groups', 'household_size', 'member_type_count'],
    name: 'Household',
    endpoint: 'household/all_household/',
  },
  unit: {
    fields: ['attr_', 'rent', 'ami', 'name', 'unit_type', 'unit_square_feet'],
    name: 'Unit',
    endpoint: 'property_portfolio/units/',
  },
  building: {
    fields: [
      'attr_',
      'address',
      'affordable_square_feet',
      'building_identification',
      'building_number',
      'city',
      'county',
      'name',
      'placed_in_service_date',
      'project_identification',
      'state',
      'total_square_feet',
      'type_of_building',
      'zip_code',
    ],
    name: 'Building',
    endpoint: 'property_portfolio/buildings/',
  },
  property: {
    fields: [
      'attr_',
      'contact_name',
      'set_aside',
      'contact_phone_number',
      'fax_number',
      'name',
      'property_email',
      'form_fill_color',
    ],
    name: 'Property',
    endpoint: 'property_portfolio/property/',
  },
  propertyownercompany: {
    fields: ['name'],
    name: 'Company',
    endpoint: 'property_portfolio/property_owner_company/',
  },
  tichasanswers: {
    fields: ['name', 'formula'],
    name: 'TIC Has Answers',
    endpoint: 'tic_flex/tic_has_answers/',
  },
  ticmatchanswers: {
    fields: ['name', 'formula'],
    name: 'TIC Match Answers',
    endpoint: 'tic_flex/tic_match_answers/',
  },
};

export const LIST_RENDER_TYPES = [
  'JoinedStringRender',
  'ListRender',
  'NumberedListRender',
];
